import type { ReactNode } from 'react';
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { useConsoleI18n } from './bind';

export type CalloutTone = 'info' | 'success' | 'warning' | 'error';

const ICON = { info: Info, success: CircleCheck, warning: TriangleAlert, error: CircleAlert };

/**
 * An inline message in the page flow (a warning above a form, a note under a heading), as opposed to a toast. A warning
 * or error is an `alert`, info and success a `status`; the tone is in the icon and the words too, never colour alone.
 * `action` sits at the end (a "Fix" button); `onClose` adds a dismiss button.
 */
export function Callout({
  tone = 'info',
  title,
  action,
  onClose,
  className,
  children,
}: {
  tone?: CalloutTone;
  title?: ReactNode;
  action?: ReactNode;
  onClose?: () => void;
  className?: string;
  children?: ReactNode;
}) {
  const { t } = useConsoleI18n();
  const Icon = ICON[tone];
  return (
    <div
      className={['nyk-callout', `nyk-callout-${tone}`, className].filter(Boolean).join(' ')}
      role={tone === 'warning' || tone === 'error' ? 'alert' : 'status'}
      data-tone={tone}
    >
      <Icon className="nyk-callout-icon" size={18} aria-hidden="true" />
      <div className="nyk-callout-body">
        {title && <b className="nyk-callout-title">{title}</b>}
        {children && <div>{children}</div>}
      </div>
      {action && <div className="nyk-callout-action">{action}</div>}
      {onClose && (
        <button
          type="button"
          className="nyk-callout-close"
          aria-label={t('common.dismiss')}
          onClick={onClose}
        >
          ×
        </button>
      )}
    </div>
  );
}
